import { Ros, Topic, } from "roslib";
import { DEFAULT_TIMEOUT_MS } from "./consts";
import {
  IControlSource,
  SocketEvent,
  SocketEventHandler,
  Subscriber,
} from "./base";
import { AnyPlainObject } from "../common/misc";
import { ControlSource } from ".";
import { wsAddress } from "../address";

// Reconnect/liveness tuning. roslib's Ros does NOT reconnect on its own — it only
// emits 'close' — so we drive reconnection here. Liveness is driven by the `/drip`
// topic (an empty message published ~1Hz by the `drip` service): a browser WebSocket
// won't fire 'close' promptly when the underlying TCP dies silently, so without a
// steady inbound signal the socket just hangs for minutes. When drips stop arriving
// we force the socket closed, which triggers a reconnect.
const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 10_000;
const LIVENESS_CHECK_MS = 2_000;
const LIVENESS_TIMEOUT_MS = 15_000;
// Dedicated liveness topic + its type (std_msgs/Empty; ROS2 rosbridge uses the
// `msg`-qualified form). Subscribed with an explicit type so we skip the rosapi
// getTopicType round-trip.
const DRIP_TOPIC = "/drip";
const DRIP_TYPE = "std_msgs/msg/Empty";

const isPrimitiveType = (value: any) =>
  ["string", "number", "boolean"].includes(typeof value);

const isPrimitiveTypeMsg = (msg: AnyPlainObject) => {
  let keys = Object.keys(msg);

  return (
    keys.length === 1 && keys.includes("data") && isPrimitiveType(msg.data)
  );
};

const unNestDataFieldIfPrimitive = (msg: AnyPlainObject) =>
  isPrimitiveTypeMsg(msg) ? msg.data : msg;

export class ROSLibJSControlSource implements IControlSource {
  socket!: Ros;
  publishers!: { [key: string]: Topic<any> };
  subscribers!: {
    [key: string]: { listener: Topic<any>; handler: Subscriber }[];
  };
  // Connection lifecycle (only managed when this source owns the socket, i.e. was
  // constructed from a url — not via fromSocket, which shares an externally-owned Ros).
  private url?: string;
  private closed = false;
  private reconnectAttempts = 0;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private heartbeatTimer?: ReturnType<typeof setInterval>;
  private lastActivityAt = 0;
  private dripListener?: Topic<any>;

  constructor(
    host: string,
    {
      timeoutMs = DEFAULT_TIMEOUT_MS,
      port = ControlSource.ROSLIBJS.port,
      useSSL = true,
      token,
      subpath,
    }: {
      timeoutMs?: number;
      /** `null` omits the port — used for same-origin gateway URLs. */
      port?: number | null;
      useSSL?: boolean;
      token?: string;
      /** Path prefix on the gateway, e.g. "api/ros" or "arc/ARC-1100/api/ros". */
      subpath?: string;
    } = {}
  ) {
    if (typeof host !== "string" || !host)
      throw new Error(
        `Expected address as non-empty string. Got ${host} instead`
      );

    const url = wsAddress({ host, port, useSSL, token, subpath });
    this.url = url;
    this.socket = new Ros({ url: this.url });

    this._initialize();
    this._manageConnection();
  }

  static fromSocket(
    socket: Ros,
    timeoutMs: number = DEFAULT_TIMEOUT_MS
  ): ROSLibJSControlSource {
    const instance = Object.create(
      ROSLibJSControlSource.prototype
    ) as ROSLibJSControlSource;
    instance.socket = socket;
    instance._initialize();
    // No _manageConnection: the socket is owned/managed by whoever constructed it.
    return instance;
  }

  private _initialize(): void {
    this.socket.on("error", (error: unknown) => {
      console.error("Error when connecting to rosbridge:", error);
    });

    this.publishers = {};
    this.subscribers = {};
  }

  /**
   * Drive reconnection + liveness for a socket we own. roslib re-subscribes/re-advertises
   * its Topics automatically on the next 'connection' (reconnect_on_close defaults to true),
   * so we only need to re-establish the underlying socket and the drip listener.
   */
  private _manageConnection(): void {
    this._subscribeDrip();
    this.socket.on("connection", () => {
      this.reconnectAttempts = 0;
      this._startHeartbeat();
    });
    this.socket.on("close", () => {
      this._stopHeartbeat();
      this._scheduleReconnect();
    });
  }

  /**
   * Subscribe to the liveness tap. Each drip refreshes lastActivityAt; the watchdog
   * force-reconnects when they stop. Created once with an explicit type (no rosapi
   * round-trip); roslib re-subscribes it automatically after a reconnect.
   */
  private _subscribeDrip(): void {
    this.dripListener = new Topic({
      ros: this.socket,
      name: DRIP_TOPIC,
      messageType: DRIP_TYPE,
    });
    this.dripListener.subscribe(() => {
      this.lastActivityAt = Date.now();
    });
  }

  private _scheduleReconnect(): void {
    if (this.closed || this.reconnectTimer || !this.url || this.socket.isConnected) return;
    const delay = Math.min(
      RECONNECT_BASE_MS * 2 ** this.reconnectAttempts,
      RECONNECT_MAX_MS
    );
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      if (this.closed || !this.url || this.socket.isConnected) return;
      try {
        // roslib no-ops if the transport is still open; close() above ensured it isn't.
        this.socket.connect(this.url);
      } catch (err) {
        console.error("rosbridge reconnect failed:", err);
        this._scheduleReconnect();
      }
    }, delay);
  }

  private _startHeartbeat(): void {
    this._stopHeartbeat();
    this.lastActivityAt = Date.now();
    this.heartbeatTimer = setInterval(() => {
      if (!this.socket.isConnected) return;
      if (Date.now() - this.lastActivityAt > LIVENESS_TIMEOUT_MS) {
        console.warn(
          `rosbridge liveness timed out (no drips for ${LIVENESS_TIMEOUT_MS}ms); forcing reconnect`,
        );
        this._dropDeadConnection();
      }
    }, LIVENESS_CHECK_MS);
  }

  private _stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = undefined;
    }
  }

  /** Force a hung socket closed; the 'close' handler then schedules the reconnect. */
  private _dropDeadConnection(): void {
    this._stopHeartbeat();
    try {
      this.socket.close();
    } catch {
      /* ignore */
    }
    // close() may not fire 'close' promptly on a dead link — schedule directly too.
    this._scheduleReconnect();
  }

  close() {
    this.closed = true;
    this._stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    this.socket.close();
  }

  isConnected() {
    return this.socket.isConnected;
  }

  onDisconnect(cb: SocketEventHandler) {
    this.socket.on("disconnect", cb);
  }

  onConnect(cb: SocketEventHandler) {
    this.socket.on("connection", cb);
  }

  onError(cb: SocketEventHandler) {
    this.socket.on("error", cb);
  }

  registerPublishers(event: string, messageType: string) {
    if (this.publishers[event]) return;

    let topicOptions = {
      ros: this.socket,
      name: event,
      messageType,
    };

    this.publishers[event] = new Topic(topicOptions);
  }

  async send(event: string, data = {}, messageType = "") {
    if (!this.socket) return;

    if (!messageType) {
      // Get the actual topic type from ROS
      messageType = await this.getTopicType(event).catch((err) => {
        console.log('failed to get topic type: error ', err)
        return ""
      });
    }

    if (!this.publishers[event]) {
      this.registerPublishers(event, messageType);
    }

    this.publishers[event].publish(
      isPrimitiveType(data) ? { data } : data
    );
  }

  private async getTopicType(topic: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.socket.getTopicType(
        topic,
        (type: string) => resolve(type),
        (error: string) => reject(new Error(error))
      );
    });
  }

  async subscribe(event: string, handler: Subscriber) {
    if (!this.socket || this.subscribers[event]) return;

    // Get the actual topic type from ROS
    console.log('getting topic type for ', event)
    const messageType = await this.getTopicType(event).catch((err) => {
      console.log('failed to get topic type: error ', err)
      return ""
    });
    console.log('got topic type for', event)
    console.log('subscribing', event, ': got messageType', messageType)

    const listener = new Topic({
      ros: this.socket,
      name: event,
      messageType,
    });

    listener.subscribe((msg: AnyPlainObject) => handler(unNestDataFieldIfPrimitive(msg)));
    // listener.subscribe((msg) => console.log(unNestDataFieldIfPrimitive(msg)));
    if (!this.subscribers[event]) {
      this.subscribers[event] = [];
    }

    this.subscribers[event].push({ listener, handler });
  }

  unsubscribe(event: string, handler: Subscriber) {
    if (!this.subscribers[event]) return;

    const index = this.subscribers[event].findIndex(
      (s) => s.handler === handler
    );

    if (index < 0) return;

    this.subscribers[event][index].listener.unsubscribe();
    this.subscribers[event].splice(index, 1);
  }
}

export type ROSLibJSControlSourceOptions = {
  timeoutMs?: number;
  /** `null` omits the port — used for same-origin gateway URLs. */
  port?: number | null;
  useSSL?: boolean;
  token?: string;
  /** Path prefix on the gateway, e.g. "api/ros" or "arc/ARC-1100/api/ros". */
  subpath?: string;
};

export type ROSLibJSControlSourceArgs = ConstructorParameters<
  typeof ROSLibJSControlSource
>;
